/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner
} from 'react-bootstrap';
import { BsCaretRightFill } from 'react-icons/bs';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';
import { FaLocationDot } from 'react-icons/fa6';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ButtonData } from '../../../components/buttonGroup/MultiSelectButtonGroup';
import { MultiFilterGroup } from '../../../components/filterGroup/MultiFilterGroup';
import { PageMeta } from '../../../components/meta/PageMeta';
import LocationModal from '../../../components/modals/LocationModal';
import { DateFilterField } from '../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../components/tables/filterForms/FilterForm';
import { FormSelect, FormSelectOptions } from '../../../components/tables/filterForms/FormSelect';
import { useDataContext } from '../../../context';
import { useNavbar } from '../../../context';
import { useMyLocationContext } from '../../../context';
import { useToast } from '../../../context';
import { useGetThemesSimpleQuery } from '../../../services/endpoints/content/theme';
import { useGetColleaguesSimpleQuery } from '../../../services/endpoints/people/user';
import { useGetVenuesSimpleQuery } from '../../../services/endpoints/places/venue';
import { useSearchCoursesQuery } from '../../../services/endpoints/schedule/course';
import { LatLng } from '../../../services/helper';
import { Filter, GetQueryParams } from '../../../types/api.type';
import { Course } from '../../../types/course.type';
import { getCurrentDateOnly } from '../../../utils/dateUtils';
import { FadeIn } from '../../animations/FadeIn';
import { CookingCampsMarker, EventsMarker, SchoolProgramMarker } from '../../maps/components';
import { FilterNavbar } from '../../navbar/FilterNavbar/FilterNavbar';
import { StyleGreen } from './CourseDates';
import CourseMap from './CourseMap';
import CourseResults from './CourseResults';
import './CourseSearch.css';
import CourseSearchBar from './CourseSearchBar';
import { CourseLink } from './CourseVenueDistance';
import SelectedCourse from './SelectedCourse';
import { Field } from 'formik';
import MapFilterOverlay from './MapFilterOverlay';
import CourseMapLegend from './CourseMapLegend';

interface CourseSearchProps {}

export const getValueFromFilters = (field: string, operator?: string, filters?: Filter[]) => {
  return filters?.find((f) => f.field === field && (operator ? operator === f.operator : true))
      ?.value;
};

const sessionTypes = ['after_school', 'camps', 'events', 'online'];

const CourseSearch: React.FC<CourseSearchProps> = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { locationOptions, getLocationOptions } = useDataContext();
  const { myLocation: preferredLocation } = useMyLocationContext();
  const [searchParams] = useSearchParams();
  const defaultScope = 'upcoming';
  const [showModal, setShowModal] = useState(false);

  // searchParams
  const sessionType = searchParams.get('session_type') ?? '';
  const isOnline = sessionType === 'online';
  const listView = isOnline || (searchParams.get('view') ?? '') === 'list';
  const search = searchParams.get('search');
  const origin = searchParams.get('origin');
  const location_id = searchParams.get('location_id');
  const venue_id = searchParams.get('venue_id');

  const { pageTitle, pageDescription } = useMemo(() => {
    if (sessionType === 'after_school') {
      return {
        pageTitle: 'Before & After School Cooking Programs',
        pageDescription:
            'Enroll your child in our award-winning after school cooking programs! On-campus and online after school cooking classes that build culinary confidence.'
      };
    }
    if (sessionType === 'camps') {
      return {
        pageTitle: 'Cooking Camps for Kids & Teens',
        pageDescription:
            'Holiday & summer cooking camps for kids and teens! In-person, online, and at-home camps that develop a passion for cooking and healthy food. Enroll today!'
      };
    }
    if (sessionType === 'online') {
      return {
        pageTitle: 'Online Cooking Classes for Kids & Teens',
        pageDescription:
            "Cultivate your child's culinary curiosity with our online cooking classes for kids! Interactive online cooking courses that teach cooking and baking basics."
      };
    }
    return { pageTitle: 'Session Results', pageDescription: undefined };
  }, [sessionType]);

  // if user searches with a location (origin), then use it, otherwise if they have a location set, use that, otherwise use no origin search
  const originFilter =
      origin || preferredLocation?.location
          ? {
            field: 'origin',
            operator: 'equals',
            value: origin || `${preferredLocation?.location.lat}_${preferredLocation?.location.lng}`
          }
          : undefined;
  const searchFilter =
      search && search !== '' ? { field: 'search', operator: 'contains', value: search } : undefined;
  const locationFilter =
      location_id && location_id !== ''
          ? { field: 'location_id', operator: 'equals', value: location_id }
          : undefined;
  const venueFilter =
      venue_id && venue_id !== ''
          ? { field: 'venue_id', operator: 'equals', value: venue_id }
          : undefined;

  const urlScope = sessionTypes.includes(sessionType) ? [sessionType] : [];
  const [scope, setScope] = useState<string[]>([defaultScope, ...urlScope]);
  const [buttons] = useState<ButtonData[]>([
    {
      key: 'after_school',
      title: 'School Programs',
      icon: (
          <SchoolProgramMarker
              height={20}
              width={20}
          />
      ),
      btnVariant: 'outline-sfc-pink'
    },
    {
      key: 'camps',
      title: 'Cooking Camps',
      icon: (
          <CookingCampsMarker
              height={20}
              width={20}
          />
      ),
      btnVariant: 'outline-sfc-orange'
    },
    {
      key: 'events',
      title: 'Events',
      icon: (
          <EventsMarker
              height={20}
              width={20}
          />
      ),
      btnVariant: 'outline-sfc-green'
    }
  ]);
  const [filters, setFilters] = useState<GetQueryParams>({
    scope: defaultScope,
    data: {
      filterBy: [
        ...(originFilter && sessionType !== 'online' ? [originFilter] : []),
        ...(searchFilter ? [searchFilter] : []),
        ...(locationFilter ? [locationFilter] : []),
        ...(venueFilter ? [venueFilter] : [])
      ]
    }
  });
  const [selectedLocations, setSelectedLocations] = useState<
      { location_ids: number[] } | undefined
  >();
  const {
    data: instructors,
    error: instructorsError,
  } = useGetColleaguesSimpleQuery(selectedLocations);
  const { data: themes, error: themeError } = useGetThemesSimpleQuery();
  const {
    data: venues,
    error: venuesError,
  } = useGetVenuesSimpleQuery(selectedLocations);
  const [venueOptions, setVenueOptions] = useState<FormSelectOptions[]>([]);

  const {
    data: courses,
    isLoading,
    error,
    isFetching,
  } = useSearchCoursesQuery({ ...filters, scopes: scope });
  const [showDistance, setShowDistance] = useState<boolean>(sessionType !== 'online');

  useEffect(() => {
    // Skip when franchise/microsite links pass ?origin= — search already has a location context.
    setShowModal(!preferredLocation && !origin);
  }, [preferredLocation, origin]);

  useEffect(() => {
    if (!origin || sessionType === 'online') return;

    const newOriginFilter = {
      field: 'origin',
      operator: 'equals',
      value: origin
    };

    setFilters((prev) => {
      const filterBy = prev.data?.filterBy ?? [];
      const originIndex = filterBy.findIndex((f) => f.field === 'origin');
      const currentValue = originIndex >= 0 ? String(filterBy[originIndex].value) : undefined;
      if (currentValue === origin) return prev;

      const copy = JSON.parse(JSON.stringify(prev)) as GetQueryParams;
      const nextFilterBy = copy.data?.filterBy ?? [];
      const nextOriginIndex = nextFilterBy.findIndex((f) => f.field === 'origin');

      if (nextOriginIndex >= 0) {
        nextFilterBy[nextOriginIndex] = newOriginFilter;
      } else {
        nextFilterBy.push(newOriginFilter);
      }

      return { ...copy, data: { ...copy.data, filterBy: nextFilterBy } };
    });
  }, [origin, sessionType]);

  useEffect(() => {
    if (!preferredLocation || sessionType === 'online' || origin) return;

    const newOriginFilter = {
      field: 'origin',
      operator: 'equals',
      value: `${preferredLocation.location.lat}_${preferredLocation.location.lng}`
    };

    setFilters((prev) => {
      const copy = JSON.parse(JSON.stringify(prev)) as GetQueryParams;
      const filterBy = copy.data?.filterBy ?? [];
      const originIndex = filterBy.findIndex((f) => f.field === 'origin');

      if (originIndex >= 0) {
        filterBy[originIndex] = newOriginFilter;
      } else {
        filterBy.push(newOriginFilter);
      }

      return { ...copy, data: { ...copy.data, filterBy } };
    });
  }, [preferredLocation, origin]);

  useEffect(() => {
    if (venues) {
      setVenueOptions(venues.map((i) => ({ value: i.id, label: i.title })) ?? []);
    }
  }, [venues]);

  useEffect(() => {
    if (filters) {
      let location_id_value = filters?.data?.filterBy?.find((f) => f.field === 'location_id')
          ?.value as unknown as undefined | { label: string; value: number }[];
      let location_ids = location_id ? [+location_id] : location_id_value?.map?.((s) => s.value);
      setSelectedLocations(location_ids && location_ids.length > 0 ? { location_ids } : undefined);
    }
  }, [filters]);

  const toggleLocationFilter = () => {
    setFilters((old) => {
      let copy = JSON.parse(JSON.stringify(old)) as GetQueryParams;
      let originIndex = copy?.data?.filterBy?.findIndex((f) => f.field === 'origin') ?? -1;
      if (originIndex === -1 && originFilter) {
        if (copy.data?.filterBy) {
          copy.data?.filterBy?.push(originFilter);
        } else {
          if (copy.data) {
            copy.data.filterBy = [originFilter];
          } else {
            copy.data = { filterBy: [originFilter] };
          }
        }
        setShowDistance(true);
      } else {
        copy.data?.filterBy?.splice(originIndex, 1);
        setShowDistance(false);
      }
      return copy;
    });
  };

  const getValueFromFilter = (field: string, operator?: string) => {
    return getValueFromFilters(field, operator, filters.data?.filterBy);
  };

  const filterFormFields: FilterField[] = [
    ...(sessionType === 'online'
        ? []
        : [
          {
            name: 'origin',
            label: 'Location',
            element: (
                <Form.Group>
                  <Form.Label>Location</Form.Label>
                  <InputGroup>
                    <Button
                        size="sm"
                        variant="outline-primary"
                        className={`text-uppercase w-100 ${showDistance ? 'active' : ''}`}
                        onClick={toggleLocationFilter}
                    >
                      <FaLocationDot
                          size={16}
                          style={StyleGreen}
                          className={`green mb-1 me-2`}
                      />
                      {`${showDistance ? "Don't " : ''}Use My Location`}
                    </Button>
                  </InputGroup>
                </Form.Group>
            )
          },
          {
            name: 'distance',
            operator: 'lesser_than',
            type: 'number',
            value: getValueFromFilter('distance')?.toString(),
            element: showDistance ? (
                <Form.Group controlId="distance">
                  <Form.Label>Max Miles</Form.Label>
                  <Field
                      as={Form.Control}
                      type="number"
                      name="distance"
                      placeholder="Approximate, default 20"
                  />
                </Form.Group>
            ) : <></>
          },
          {
            name: 'none',
            label: '',
            element: <hr className="m-0" />
          }
        ]),
    // TODO: decide whether to include here as part of filter form or seprate out and use quick scope buttons on list view as well
    // {
    //   name: "course_type",
    //   element: (
    //     <FormSelect
    //       name="course_type"
    //       label="Session Type"
    //       value={sessionTypes?.includes(sessionType) ? {value: sessionType, label: titleCase(sessionType)} : undefined}
    //       options={sessionTypes?.map(i => ({value: i, label: titleCase(i)})) ?? []}
    //     />
    //   ),
    // },
    {
      name: 'location_id',
      operator: 'includes',
      value: getLocationOptions(location_id ?? ''),
      element: (
          <FormSelect
              name="location_id"
              label="Location"
              multiple={true}
              options={locationOptions ?? []}
          />
      )
    },
    {
      name: 'venue_id',
      label: 'Venue/School',
      element: (
          <FormSelect
              name="venue_id"
              label="School/Venue"
              // multiple={true} ?
              options={venueOptions}
              loadingError={venuesError ? 'Error loading school/venues' : undefined}
          />
      )
    },
    {
      name: 'starts_from',
      id: 'starts_on',
      operator: 'greater_equal_than',
      value: getValueFromFilter('starts_on', 'greater_equal_than')?.toString(),
      element: (
          <DateFilterField
              name="starts_from"
              label="Date: From"
          />
      )
    },
    {
      name: 'starts_to',
      id: 'starts_on',
      operator: 'lesser_equal_than',
      value: getValueFromFilter('starts_on', 'lesser_equal_than')?.toString(),
      element: (
          <DateFilterField
              name="starts_to"
              label="To"
              min={getCurrentDateOnly()}
          />
      )
    },
    {
      name: 'instructor_id',
      type: 'number',
      element: (
          <FormSelect
              name="instructor_id"
              label="Instructor"
              options={
                  instructors?.map((i) => ({
                    value: i.id,
                    label: i.full_name ?? `${i.first_name} ${i.last_name}`
                  })) ?? []
              }
              loadingError={instructorsError ? 'Error loading instructors' : undefined}
          />
      )
    },
    {
      name: 'theme_id',
      type: 'number',
      element: (
          <FormSelect
              name="theme_id"
              label="Theme"
              options={themes?.map((i) => ({ value: i.id, label: i.name })) ?? []}
              loadingError={themeError ? 'Error loading themes' : undefined}
          />
      )
    }
  ];

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    // TODO condense this and combine with other similar filter setting functions throughout this search component siblings
    if (filterBy.length === 0) {
      return;
    }
    let distanceIndex = filterBy.findIndex((f) => f.field === 'distance');
    if (distanceIndex !== -1) {
      if (!preferredLocation?.location) {
        filterBy.splice(distanceIndex, 1);
      }
    }
    let courseTypeFilter = filters?.data?.filterBy?.find((f) => f.field === 'course_type');
    let origin_Filter = filters?.data?.filterBy?.find((f) => f.field === 'origin');
    if (!origin_Filter) {
      origin_Filter = { field: 'origin', operator: 'includes', value: originFilter?.value ?? '' };
    }
    let baseFilter = filterBy as Filter[];
    if (courseTypeFilter) {
      baseFilter.push(courseTypeFilter);
    }
    if (origin_Filter) {
      baseFilter.push(origin_Filter);
    }
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy: baseFilter
      }
    }));
  };

  const resetFilters = (originalReset: () => void) => {
    let copy = JSON.parse(JSON.stringify(filters)) as GetQueryParams;
    let filter = copy?.data?.filterBy?.find((f) => f.field === 'course_type');
    let filterBy = filter ? [filter] : undefined;
    if (originFilter) {
      filterBy = filterBy ? [...filterBy, originFilter] : [originFilter];
    }
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
    setShowDistance(false);
    originalReset();
  };

  useEffect(() => {
    if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, error, isLoading]);

  if (isLoading)
    return (
        <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '300px' }}
        >
          <Spinner
              animation="border"
              variant="primary"
          />
        </div>
    );

  return (
      <>
        <PageMeta
            title={pageTitle}
            description={pageDescription}
        />
        <FilterNavbar inHome={true} />

        <CourseMapSearch
            listView={listView}
            hideViewToggle={isOnline}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
                  reset={resetFilters}
              />
            }
            onFiltersChanged={setFilters}
            filters={filters}
            data={courses?.results}
            isLoading={isFetching}
            setScope={setScope}
            scope={scope}
            scopes={buttons}
        />

        <LocationModal
            show={showModal}
            setShow={setShowModal}
            title="Set Your Location"
            message="To search for Sticky Fingers Cooking Sessions near you, please select a location or use your current location."
            placeholder="Search City or Zip Code"
            onClose={() => {
              setShowModal(false);
              navigate('/');
            }}
        />
      </>
  );
};

interface CourseMapSearchProps {
  listView: boolean;
  hideViewToggle?: boolean;
  filterForm?: ReactElement;
  onFiltersChanged?: React.Dispatch<React.SetStateAction<GetQueryParams>>;
  filters?: GetQueryParams;
  data?: Course[];
  scope: string[];
  setScope: React.Dispatch<React.SetStateAction<string[]>>;
  scopes?: ButtonData[];
  isLoading?: boolean;
}

const CourseMapSearch: React.FC<CourseMapSearchProps> = ({
                                                           listView: showList,
                                                           hideViewToggle = false,
                                                           filterForm,
                                                           onFiltersChanged,
                                                           filters,
                                                           data,
                                                           scope,
                                                           setScope,
                                                           scopes,
                                                           isLoading
                                                         }) => {
  const { setFilterNavContent, setFilterNavOverlap, activeNavbar, setActiveNavbar } = useNavbar();
  const { myLocation: location } = useMyLocationContext();

  const [selectedCourse, setSelectedCourse] = useState<Course>();
  const [listView, setListView] = useState<boolean>(showList);

  const originLatLng = useMemo<LatLng | undefined>(() => {
    const originValue = getValueFromFilters('origin', undefined, filters?.data?.filterBy) as
        | string
        | undefined;
    if (typeof originValue === 'string' && originValue.includes('_')) {
      const [lat, lng] = originValue.split('_').map(Number);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    }
    if (location?.location) return { lat: location.location.lat, lng: location.location.lng };
    return undefined;
  }, [filters, location]);

  const [selectedLocation, setSelectedLocation] = useState<LatLng | undefined>(originLatLng);

  useEffect(() => {
    setListView(showList);
  }, [showList]);

  useEffect(() => {
    const venue = selectedCourse?.venue;
    if (venue?.latitude && venue?.longitude) {
      setSelectedLocation({ lat: venue?.latitude, lng: venue?.longitude });
    } else {
      setSelectedLocation(originLatLng);
    }
  }, [selectedCourse, originLatLng]);

  useEffect(() => {
    if (filterForm) {
      setFilterNavContent(filterForm);
      setFilterNavOverlap(true);
    }
  }, [filterForm]);

  const toggleView = () => {
    setListView(!listView);
  };

  const toggleScope = (s: string) => {
    setScope((old) => {
      let copy = [...old];
      if (copy.includes(s)) {
        copy.splice(copy.indexOf(s), 1);
      } else {
        copy.push(s);
      }
      return copy;
    });
  };

  const useMyLocation = getValueFromFilters('origin', undefined, filters?.data?.filterBy);

  const openFiltersButton = (
      <Button
          size="sm"
          variant="outline-primary"
          className={`text-uppercase flex-shrink-0 ${activeNavbar === 'filter' ? 'active' : ''}`}
          onClick={() => setActiveNavbar('filter')}
      >
        <HiOutlineAdjustmentsHorizontal size={20} className="me-1" />
        Open Filters
      </Button>
  );

  const listViewToggleButton = !hideViewToggle && (
      <Button
          variant="primary"
          size="sm"
          className="text-uppercase flex-shrink-0"
          style={{ height: '38px' }}
          onClick={toggleView}
      >
        {listView ? 'Map View' : 'List View'}
      </Button>
  );

  const findUsNationwideLink = (
      <CourseLink
          url={'/nationwide'}
          content={
            <span className="text-uppercase text-nowrap" style={{ fontSize: '14px' }}>
              Find Us Nationwide{' '}
              <BsCaretRightFill style={{ top: '-1px', position: 'relative' }} />
            </span>
          }
          color={'blue'}
      />
  );

  return (
      <FadeIn>
        <Container
            fluid
            className={`course-search px-3 px-md-4 mb-4 ${!listView ? 'course-search--map-view' : ''}`}
        >

          {/* Title + search left | Action column right */}
          <Row className="mb-2 pt-3 course-search-header-row">
            <Col xs={12} lg={5}>
              <h1 className="course-search-title text-info">
                <b>Find cooking classes near you!</b>
              </h1>
              <h3 className="course-search-subtitle text-muted d-none d-lg-block">Everywhere you want us to be</h3>
              <CourseSearchBar
                  filters={filters}
                  setFilters={onFiltersChanged}
              />
            </Col>

            {/* DESKTOP layout */}
            <Col xs={12} lg={7} className="d-none d-lg-flex">
              <div className="d-flex align-items-stretch justify-content-between w-100 gap-2 position-relative">
                {/* Left: Open Filters at top, List View at bottom */}
                <div className="d-flex flex-column align-items-start justify-content-between pb-2">
                  {openFiltersButton}
                  {listViewToggleButton}
                </div>

                {/* Center */}
                {!listView && (
                    <div
                        style={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 2
                        }}
                    >
                      <MapFilterOverlay
                          scopes={scopes}
                          activeScope={scope}
                          onToggle={toggleScope}
                      />
                    </div>
                )}
                {listView && (
                    <div className="flex-shrink-0">
                      <MultiFilterGroup
                          filterButtons={scopes}
                          activeScope={scope}
                          onFilterSelect={(filter: string) => {
                            toggleScope(filter);
                          }}
                      />
                    </div>
                )}

                {/* Right: Applied Filters at top, Find Us Nationwide at bottom */}
                <div
                    className="d-flex flex-column align-items-end justify-content-between pb-2"
                    style={{ minWidth: 0, maxWidth: '40%' }}
                >
                  <CourseSearchBar
                      filters={filters}
                      setFilters={onFiltersChanged}
                      filtersOnly={true}
                  />
                  {findUsNationwideLink}
                </div>
              </div>
            </Col>

            {/* MOBILE layout */}
            <Col xs={12} className="d-flex d-lg-none flex-column">
              {/* Row 1: List View (left) + Open Filters (right) */}
              <div className="d-flex align-items-center justify-content-between w-100 gap-2 mb-2">
                {listViewToggleButton ?? <div />}
                {openFiltersButton}
              </div>
              {/* Row 2: Applied Filters, left-aligned */}
              <div className="course-search-applied-filters mb-2">
                <CourseSearchBar
                    filters={filters}
                    setFilters={onFiltersChanged}
                    filtersOnly={true}
                />
              </div>
              {/* Row 3 (list view only): Session type filters */}
              {listView && (
                  <div className="course-search-scope-filters mb-2 d-flex justify-content-center">
                    <MapFilterOverlay
                        scopes={scopes}
                        activeScope={scope}
                        onToggle={toggleScope}
                    />
                  </div>
              )}
            </Col>
          </Row>

          {/* Results */}
          <Row>
            <Col
                lg={listView ? 12 : 5}
                className={!listView ? 'order-2 order-lg-1' : undefined}
            >
              <SelectedCourse
                  course={selectedCourse}
                  onClose={() => setSelectedCourse(undefined)}
              />
              {isLoading && (
                  <span className="ms-1">
                    Loading ... <Spinner size="sm" />
                  </span>
              )}
              <CourseResults
                  listView={listView}
                  courses={data ?? []}
                  selectedCourseId={selectedCourse?.id}
                  onClick={(course: Course) => setSelectedCourse(course)}
              />
            </Col>
            {!listView && (
                <Col lg={7} className="order-1 order-lg-2 mb-3 mb-lg-0">
                  <div className="d-lg-none d-flex justify-content-center course-search-map-scope-filters mt-2 mb-2">
                    <MapFilterOverlay
                        scopes={scopes}
                        activeScope={scope}
                        onToggle={toggleScope}
                    />
                  </div>
                  <CourseMap
                      courses={data ?? []}
                      location={selectedLocation}
                      onVenueClick={setSelectedCourse}
                      selectedCourseId={selectedCourse?.id}
                  />
                  <CourseMapLegend scopes={scopes} />
                </Col>
            )}
          </Row>
        </Container>
      </FadeIn>
  );
};

export default CourseSearch;
